import {
  DEEP_LINK_SCHEME,
  DEEP_LINK_TIMEOUT_IOS,
  DEEP_LINK_TIMEOUT_ANDROID,
} from '../constants/appStoreLinks';

/**
 * Определяет тип устройства
 * @returns {'ios' | 'android' | 'desktop'}
 */
export const detectDevice = () => {
  const ua = navigator.userAgent;

  if (/iPhone|iPad|iPod/i.test(ua)) {
    return 'ios';
  } else if (/Android/i.test(ua)) {
    return 'android';
  }
  return 'desktop';
};

/**
 * Проверяет, является ли устройство мобильным
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  const device = detectDevice();
  return device === 'ios' || device === 'android';
};

/**
 * Гибридная обработка deep link с fallback на App Store
 * Пытается открыть приложение через deep link, если не получается - редиректит на App Store
 *
 * @param {string} appStoreUrl - URL для редиректа в App Store (AppMetrica сам определит платформу)
 * @param {string} deepLink - Deep link схема (по умолчанию whiteswan://main)
 * @returns {boolean} - true если deep link обработан, false если нужно обработать по-другому (например, открыть модалку)
 */
export const handleDeepLink = (appStoreUrl, deepLink = DEEP_LINK_SCHEME) => {
  // Если не мобильное устройство, просто возвращаем false
  // чтобы фронтенд мог обработать (например, открыть модалку)
  if (!isMobileDevice()) {
    return false;
  }

  const device = detectDevice();

  if (device === 'ios') {
    // Для iOS используем специальный подход с улучшенной обработкой
    handleIOSDeepLink(deepLink, appStoreUrl);
    return true;
  } else if (device === 'android') {
    // Для Android используем Page Visibility API
    handleAndroidDeepLink(deepLink, appStoreUrl);
    return true;
  }

  return false;
};

/**
 * Обработка deep link для iOS
 * Пытается открыть приложение напрямую через window.location.href
 * Если приложение не установлено или не открылось - редиректит на App Store
 * @param {string} deepLink - Deep link схема
 * @param {string} appStoreUrl - URL для редиректа в App Store
 */
export function handleIOSDeepLink(deepLink, appStoreUrl, opts = {}) {
  const timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : DEEP_LINK_TIMEOUT_IOS;
  const timerSlopMs = typeof opts.timerSlopMs === 'number' ? opts.timerSlopMs : 500;

  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document); // iPadOS Safari

  if (!isIOS) {
    try {
      window.location.href = deepLink;
    } catch (e) {
      window.location.href = appStoreUrl;
    }
    return;
  }

  let appOpened = false;
  let cleaned = false;
  let redirecting = false;

  const nowFn = () =>
    window.performance && typeof performance.now === 'function' ? performance.now() : Date.now();
  const start = nowFn();

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;

    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('blur', onBlur);
  };

  const markOpened = (reason) => {
    if (appOpened) return;
    appOpened = true;
    cleanup();
  };

  const redirectToAppStore = () => {
    if (redirecting || appOpened) return;
    redirecting = true;
    cleanup();
    window.location.href = appStoreUrl;
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden' || document.hidden) {
      markOpened('visibilitychange(hidden)');
    }
  };

  const onPageHide = () => {
    markOpened('pagehide');
  };

  const onBlur = () => {
    markOpened('blur');
  };

  // Подписываемся на события ДО попытки открыть deep link
  document.addEventListener('visibilitychange', onVisibilityChange, { passive: true });
  window.addEventListener('pagehide', onPageHide, { passive: true });
  window.addEventListener('blur', onBlur, { passive: true });

  // Пытаемся открыть deep link напрямую
  try {
    window.location.href = deepLink;
  } catch (e) {
    // Если ошибка при открытии - сразу редиректим на App Store
    redirectToAppStore();
    return;
  }

  // Fallback to App Store if app didn't open
  setTimeout(() => {
    if (cleaned) return;

    const elapsed = nowFn() - start;

    // If page is hidden -> app opened
    if (document.visibilityState === 'hidden' || document.hidden) {
      markOpened('timeout(hidden)');
      return;
    }

    // iOS timer-freeze detection:
    // if callback fires much later than timeoutMs, user likely switched to the app.
    if (elapsed > timeoutMs + timerSlopMs) {
      markOpened('timeout(late)');
      return;
    }

    // Если приложение не открылось - редиректим на App Store
    if (!appOpened) {
      redirectToAppStore();
      return;
    }

    cleanup();
  }, timeoutMs);
}

/**
 * Обработка deep link для Android с использованием Page Visibility API
 * @param {string} deepLink - Deep link схема
 * @param {string} appStoreUrl - URL для редиректа в App Store
 */
const handleAndroidDeepLink = (deepLink, appStoreUrl) => {
  let pageHidden = false;
  let timeoutId = null;

  // Обработчик изменения видимости страницы
  const handleVisibilityChange = () => {
    if (document.hidden) {
      pageHidden = true;
      cleanup();
    }
  };

  // Обработчик потери фокуса (дополнительная проверка)
  const handleBlur = () => {
    pageHidden = true;
    cleanup();
  };

  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('blur', handleBlur);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  // Подписываемся на события
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('blur', handleBlur);

  // Пытаемся открыть deep link
  try {
    window.location.href = deepLink;
  } catch (e) {
    cleanup();
    window.location.href = appStoreUrl;
    return;
  }

  // Таймаут для проверки
  timeoutId = setTimeout(() => {
    if (!pageHidden) {
      // Страница все еще видима - приложение не установлено
      cleanup();
      window.location.href = appStoreUrl;
    } else {
      cleanup();
    }
  }, DEEP_LINK_TIMEOUT_ANDROID);
};
